import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  InfraHistoryResponse,
  InfraSnapshot,
  InfraThreshold,
  InfraThresholdsResponse,
} from '@/lib/types'

const SNAPSHOT_REFETCH_MS = 15_000
const HISTORY_REFETCH_MS = 60_000

export function useInfraSnapshot() {
  return useQuery<InfraSnapshot>({
    queryKey: ['infra', 'current'],
    queryFn: () => apiClient.get<InfraSnapshot>(endpoints.infra.current),
    refetchInterval: SNAPSHOT_REFETCH_MS,
  })
}

export function useInfraHistory(host: string, period: string) {
  return useQuery<InfraHistoryResponse>({
    queryKey: ['infra', 'history', host, period],
    queryFn: () => apiClient.get<InfraHistoryResponse>(endpoints.infra.history(host, period)),
    refetchInterval: HISTORY_REFETCH_MS,
    enabled: host.length > 0,
  })
}

export function useInfraThresholds() {
  return useQuery<InfraThresholdsResponse>({
    queryKey: ['infra', 'thresholds'],
    queryFn: () => apiClient.get<InfraThresholdsResponse>(endpoints.infra.thresholds),
  })
}

export function useUpdateThreshold() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (threshold: InfraThreshold) =>
      apiClient.put<void>(endpoints.infra.updateThreshold, threshold),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['infra', 'thresholds'] })
    },
  })
}
